import React, { useState, useEffect } from 'react';
import '../App.css';
import './ImageTesterView.css';
import ImageUploader from './ImageUploader';
import ResultDisplay from './ResultDisplay';

function ImageTesterView() {
  const [result, setResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isOpenCVReady, setIsOpenCVReady] = useState(false);
  const [openCVError, setOpenCVError] = useState(null);

  useEffect(() => {
    // Check if OpenCV is already loaded
    if (window.cv && window.OpenCVStatus?.loaded) {
      setIsOpenCVReady(true);
      return;
    }

    // Set up event listeners for OpenCV loading events
    const handleOpenCVLoaded = () => {
      console.log('OpenCV loaded event received');
      setIsOpenCVReady(true);
    };

    const handleOpenCVError = () => {
      console.error('OpenCV error event received');
      setOpenCVError('Failed to load OpenCV libraries. Using basic mode.');
    };

    // Listen for OpenCV loading events
    document.addEventListener('opencv-loaded', handleOpenCVLoaded);
    document.addEventListener('opencv-error', handleOpenCVError);

    // Check again after a timeout in case events were missed
    const timeoutId = setTimeout(() => {
      if (window.cv && window.OpenCVStatus?.loaded) {
        setIsOpenCVReady(true);
      } else if (!isOpenCVReady) {
        setOpenCVError('OpenCV loading timed out. Using basic mode.');
      }
    }, 5000);

    return () => {
      document.removeEventListener('opencv-loaded', handleOpenCVLoaded);
      document.removeEventListener('opencv-error', handleOpenCVError);
      clearTimeout(timeoutId);
    };
  }, [isOpenCVReady]);

  const handleImageSelected = (imageData) => {
    setIsAnalyzing(false);
    setResult(imageData);
  };

  const handleReset = () => {
    setResult(null);
  };

  return (
    <div className="image-tester-view">
      {!result ? (
        <>
          <div className="tool-labels">PRIVATE · BROWSER-BASED · FREE</div>
          
          <div className="landing-container">
            <div className="landing-text">
              <h1>AR Image Target Analyzer</h1>
              <p className="tool-description">
                Evaluate your image's suitability for AR tracking based on industry standards.
                Instantly get a detailed score, visualize feature points, and receive actionable feedback to improve your AR targets – all securely within your browser.
              </p>
            </div>

            <div className="uploader-container">
              <ImageUploader 
                onImageSelected={handleImageSelected} 
                isProcessing={isAnalyzing}
                isReady={isOpenCVReady}
                isFallbackMode={!!openCVError}
              />
            </div>
          </div>

          <div className="info-section">
            <h3>Why AR Image Analysis Matters</h3>
            <p>
              Reliable image tracking is fundamental to engaging Augmented Reality experiences. Targets with poor trackability can lead to unstable or lost tracking, frustrating users. This analyzer helps you preemptively evaluate images based on key factors: high contrast, rich and well-distributed features, and pattern uniqueness. By understanding these factors, you can optimize your targets before development.
            </p>

            <h3 style={{ marginTop: '2rem' }}>How This Analyzer Works (Client-Side)</h3> 
            <p>
              Leveraging the power of OpenCV.js and WebAssembly, this tool performs all analysis <strong>directly within your browser</strong>. Your image is never uploaded or stored. It examines:
              <ul>
                <li><b>Feature Point Detection:</b> Using the efficient ORB algorithm, it counts and maps distinct visual features (corners, unique textures).</li>
                <li><b>Contrast Analysis:</b> It measures the grayscale intensity distribution to gauge contrast, crucial for detection in diverse lighting.</li>
                <li><b>Feature Distribution:</b> It assesses how evenly features cover the image, ensuring tracking stability even with partial occlusion.</li>
                <li><b>Pattern Uniqueness:</b> It evaluates the potential for repetitive or symmetrical patterns that might confuse tracking algorithms.</li>
              </ul>
              Based on a weighted combination of these metrics, the analyzer generates a comprehensive trackability score and provides specific recommendations for improvement.
            </p>
          </div>
        </>
      ) : (
        <div className="result-container">
          <ResultDisplay result={result} onReset={handleReset} />
        </div>
      )}
    </div>
  );
}

export default ImageTesterView; 